/**
 * Local Storage utility for managing user settings
 */

export interface LocalSettings {
  timezone: string
  listMax: number
  debugMode: boolean
  deviceTimeoutHours: number
  chartEditEnabled: boolean
  compareSeparateNoticeSeen: boolean
}

const SETTINGS_KEY = 'ioag_user_settings'

/**
 * Get settings from localStorage with fallback to defaults
 */
export function getLocalSettings(): LocalSettings {
  try {
    const stored = localStorage.getItem(SETTINGS_KEY)
    if (stored) {
      const parsed = JSON.parse(stored)
      return {
        timezone: parsed.timezone || 'America/Chicago',
        listMax: typeof parsed.listMax === 'number' ? parsed.listMax : 100,
        debugMode:
          typeof parsed.debugMode === 'boolean'
            ? parsed.debugMode
            : typeof parsed.messagesVisible === 'boolean'
              ? parsed.messagesVisible
              : false,
        deviceTimeoutHours:
          typeof parsed.deviceTimeoutHours === 'number' ? Math.max(1, Math.min(48, parsed.deviceTimeoutHours)) : 6,
        chartEditEnabled: typeof parsed.chartEditEnabled === 'boolean' ? parsed.chartEditEnabled : false,
        compareSeparateNoticeSeen:
          typeof parsed.compareSeparateNoticeSeen === 'boolean' ? parsed.compareSeparateNoticeSeen : false,
      }
    }
  } catch (error) {
    console.warn('Failed to parse stored settings:', error)
  }

  // Return defaults if no stored settings or parsing failed
  return {
    timezone: 'America/Chicago',
    listMax: 100,
    debugMode: false,
    deviceTimeoutHours: 6,
    chartEditEnabled: false,
    compareSeparateNoticeSeen: false,
  }
}

/**
 * Save settings to localStorage
 */
export function saveLocalSettings(settings: Partial<LocalSettings>): void {
  try {
    const current = getLocalSettings()
    const updated = { ...current, ...settings }
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(updated))

    // Dispatch custom event to notify other components of settings change
    window.dispatchEvent(
      new CustomEvent('settingsChanged', {
        detail: updated,
      })
    )
  } catch (error) {
    console.error('Failed to save settings to localStorage:', error)
    throw error
  }
}

/**
 * Save a single setting to localStorage
 */
export function saveLocalSetting<K extends keyof LocalSettings>(key: K, value: LocalSettings[K]): void {
  saveLocalSettings({ [key]: value } as Partial<LocalSettings>)
}

/**
 * Clear all settings from localStorage
 */
export function clearLocalSettings(): void {
  try {
    localStorage.removeItem(SETTINGS_KEY)
    window.dispatchEvent(
      new CustomEvent('settingsChanged', {
        detail: getLocalSettings(), // Will return defaults
      })
    )
  } catch (error) {
    console.error('Failed to clear settings from localStorage:', error)
  }
}

/**
 * Hook into settings changes across tabs/windows
 */
export function onSettingsChange(callback: (settings: LocalSettings) => void): () => void {
  const handleChange = (event: CustomEvent<LocalSettings>) => {
    callback(event.detail)
  }

  const handleStorageChange = (event: StorageEvent) => {
    if (event.key === SETTINGS_KEY) {
      callback(getLocalSettings())
    }
  }

  // Listen for custom events (same tab changes)
  window.addEventListener('settingsChanged', handleChange as EventListener)

  // Listen for storage events (cross-tab changes)
  window.addEventListener('storage', handleStorageChange)

  // Return cleanup function
  return () => {
    window.removeEventListener('settingsChanged', handleChange as EventListener)
    window.removeEventListener('storage', handleStorageChange)
  }
}
